<template>
  <div class="folder-popup">
    <div class="folder-popup__backdrop">
      <div class="folder-popup__modal">
        <!-- Header -->
        <header class="folder-popup__header">
          <h2 class="folder-popup__title">Paper Saved In Folder</h2>
          <button
            type="button"
            class="folder-popup__close-btn"
            aria-label="Close popup"
            @click="closeAllPopupMethod"
          >
            <i class="fa fa-times-circle-o" aria-hidden="true"></i>
          </button>
        </header>

        <!-- Body -->
        <div class="folder-popup__body">
          <!-- View 1: Create New Sub-Shelf -->
          <form
            v-if="addNewBucketState"
            @submit.prevent="createNewSubBucketBeforeSubmitMethod"
          >
            <div class="folder-popup__form-group">
              <label for="shelf-name" class="folder-popup__label">
                <button
                  type="button"
                  class="folder-popup__back-btn"
                  aria-label="Go back to shelf list"
                  @click="backFolderListMethod"
                >
                  <i class="fa fa-angle-double-left" aria-hidden="true"></i>
                </button>
                Enter a name for the new shelf
              </label>
              <input
                id="shelf-name"
                v-model.trim="name"
                v-validate="'required'"
                type="text"
                name="name"
                autocomplete="off"
                placeholder="Enter shelf name"
                class="folder-popup__input"
                :class="{ 'folder-popup__input--invalid': errors.has('name') }"
                data-vv-as="name"
              />
              <small v-if="errors.has('name')" class="folder-popup__error">
                {{ errors.first('name') }}
              </small>
            </div>

            <div class="folder-popup__actions">
              <button
                type="submit"
                class="folder-popup__btn folder-popup__btn--danger"
                :disabled="submitted"
              >
                Save
              </button>
            </div>
          </form>

          <!-- View 2: Browse Existing Shelves -->
          <div v-else class="folder-popup__shelf-view">
            <div class="folder-popup__toolbar">
              <button
                type="button"
                class="folder-popup__btn folder-popup__btn--primary"
                @click="addNewFolderMethod"
              >
                Add New Shelf
              </button>
            </div>

            <div v-if="bucketListResults.length > 0" class="folder-popup__tree-wrapper">
              <bucket-tree-view :tree-data="bucketListResults"></bucket-tree-view>

              <div class="folder-popup__actions mt-4">
                <button
                  type="button"
                  class="folder-popup__btn folder-popup__btn--danger"
                  :disabled="submitted || !selectedFolderId"
                  @click="movePaperInParentFolder"
                >
                  Save
                </button>
              </div>
            </div>

            <div v-else class="folder-popup__empty-state">
              <p>You don't have any shelf.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bus } from '@/main';
import { mapGetters } from 'vuex';
import toast from '@/services/toast';
import BucketTreeView from '../paper-move-to-folder/tree-view-bucket.vue';

export default {
  name: 'PaperUploadInFolder',
  components: {
    'bucket-tree-view': BucketTreeView
  },
  props: {
    paperID: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      submitted: false,
      name: '',
      addNewBucketState: false,
      selectedFolderId: ''
    };
  },
  computed: {
    ...mapGetters(['getBucketListOfAwerackGetters']),
    bucketListResults() {
      const bucketData = this.getBucketListOfAwerackGetters;
      if (bucketData && bucketData.data && Array.isArray(bucketData.data.results)) {
        return bucketData.data.results;
      }
      return [];
    }
  },
  mounted() {
    bus.$on('getSelectFolderID', (data) => {
      this.selectedFolderId = data;
    });
  },
  beforeDestroy() {
    bus.$off('getSelectFolderID');
  },
  methods: {
    getPaperPileListAgain() {
      this.$store.dispatch('getAllPaperPileFun');
    },

    async movePaperInParentFolder() {
      if (!this.selectedFolderId) {
        toast.error('Please select a shelf first');
        return;
      }

      this.submitted = true;
      const payload = {
        pdf_paper: this.paperID,
        parent: this.selectedFolderId,
        is_folder: false
      };

      try {
        const res = await this.$store.dispatch('createBucketOfAwerackDetailsFun', payload);
        this.getPaperPileListAgain();
        this.closeAllPopupMethod();
        toast.success(res.data && res.data.msg ? res.data.msg : 'Paper moved successfully');
      } catch (err) {
        const errorMsg =
          (err.response && err.response.data && (err.response.data.user_msg || err.response.data.msg)) ||
          (err.data && (err.data.user_msg || err.data.msg)) ||
          'Failed to move paper to folder';
        toast.error(errorMsg);
      } finally {
        this.submitted = false;
      }
    },

    async createNewSubBucketBeforeSubmitMethod() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;

      this.submitted = true;
      const data = {
        name: this.name,
        is_folder: true
      };

      try {
        // Step 1: Create the new shelf
        const folderRes = await this.$store.dispatch('createBucketOfAwerackDetailsFun', data);
        const newFolderId = folderRes.data && folderRes.data.data && folderRes.data.data.id;

        // Step 2: Attach the paper to the new shelf
        const payload = {
          pdf_paper: this.paperID,
          parent: newFolderId,
          is_folder: false
        };

        const paperRes = await this.$store.dispatch('createBucketOfAwerackDetailsFun', payload);
        toast.success(paperRes.data && paperRes.data.msg ? paperRes.data.msg : 'Shelf created & paper saved');
        
        await this.$store.dispatch('getBucketListOfAwerackFun');
        this.getPaperPileListAgain();
        this.closeAllPopupMethod();
      } catch (err) {
        const errorMsg =
          (err.response && err.response.data && (err.response.data.user_msg || err.response.data.msg)) ||
          (err.data && (err.data.user_msg || err.data.msg)) ||
          'Failed to create shelf';
        toast.error(errorMsg);
      } finally {
        this.submitted = false;
      }
    },

    closeAllPopupMethod() {
      bus.$emit('savePaperPopupStateBus', false);
      bus.$emit('addPaperPilePopupStateBus', false);
      bus.$emit('uploadPaperStateBus', false);
      bus.$emit('paperSavedInFolderStateBus', false);
      document.body.classList.remove('body_right_scroll_remove');
    },

    addNewFolderMethod() {
      this.addNewBucketState = true;
      this.name = '';
      if (this.$validator) {
        this.$validator.reset();
      }
    },

    backFolderListMethod() {
      this.addNewBucketState = false;
      this.name = '';
    }
  }
};
</script>

<style lang="scss" scoped>
$font-stack: 'MyriadProRegular', 'Lato', sans-serif;
$theme-gold: #ffd400;
$accent-blue: #87cefa;
$bg-backdrop: rgba(0, 0, 0, 0.92);
$bg-input: rgba(255, 255, 255, 0.1);
$border-translucent: rgba(255, 255, 255, 0.08);
$error-color: #dc3545;
$btn-danger-bg: #b41818;
$text-light: #ffffff;

.folder-popup {
  font-family: $font-stack;

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: $bg-backdrop;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
  }

  &__modal {
    background: #000000;
    width: 100%;
    max-width: 440px;
    border-radius: 8px;
    box-shadow: 0 0 16px rgba(255, 213, 0, 0.5);
    border: 1px solid rgba(255, 213, 0, 0.2);
    overflow: hidden;
  }

  &__header {
    padding: 16px 20px;
    position: relative;
    border-bottom: 1px solid $border-translucent;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $text-light;
    margin: 0;
  }

  &__close-btn {
    background: transparent;
    border: none;
    color: $error-color;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
    padding: 0;
    transition: color 0.2s ease, transform 0.2s ease;

    &:hover {
      color: $accent-blue;
      transform: scale(1.1);
    }
  }

  &__body {
    padding: 20px;
  }

  &__form-group {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 1rem;
    color: $text-light;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__back-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: $text-light;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1.1rem;
    transition: all 0.2s ease;

    &:hover {
      background: $accent-blue;
      color: #000000;
    }
  }

  &__input {
    width: 100%;
    height: 44px;
    padding: 8px 14px;
    font-size: 1rem;
    color: $text-light;
    background: $bg-input;
    border: 1px solid transparent;
    border-radius: 6px;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: $theme-gold;
    }

    &--invalid {
      border-color: $error-color !important;
    }
  }

  &__error {
    color: $error-color;
    font-size: 0.85rem;
    margin-top: 6px;
  }

  &__toolbar {
    margin-bottom: 16px;
  }

  &__tree-wrapper {
    max-height: 320px;
    overflow-y: auto;
    padding-right: 4px;
  }

  &__empty-state {
    padding: 24px 0;
    text-align: center;
    color: rgba(255, 255, 255, 0.6);

    p {
      margin: 0;
      font-size: 0.95rem;
    }
  }

  &__actions {
    width: 100%;
  }

  &__btn {
    font-family: inherit;
    font-weight: 600;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--primary {
      padding: 6px 14px;
      font-size: 0.95rem;
      background: rgba(255, 255, 255, 0.12);
      color: $text-light;

      &:hover:not(:disabled) {
        background: $accent-blue;
        color: #22292f;
      }
    }

    &--danger {
      width: 100%;
      padding: 10px;
      font-size: 1rem;
      background: $btn-danger-bg;
      color: #fffcc4;

      &:hover:not(:disabled) {
        background: $accent-blue;
        color: #000000;
      }
    }
  }
}
</style>